'use client';

import * as React from 'react';

import { cn } from '../lib/utils';
import { Skeleton } from './skeleton';

/**
 * Presentational account widget. Kept Clerk-agnostic so both
 * `@clerk/nextjs` (academy) and `@clerk/clerk-react` (platform) consumers
 * can use it — they pass the avatar (Clerk's `<UserButton>`) and the
 * settings-click handler in as props.
 */
export interface AccountWidgetProps {
  /** Clerk's <UserButton /> or equivalent avatar element (opens account menu on click). */
  avatar: React.ReactNode;
  name?: string | null;
  email?: string | null;
  /** Opens user profile / settings modal. Typically Clerk's `openUserProfile`. */
  onSettingsClick?: () => void;
  isLoading?: boolean;
  /**
   * `full`  — avatar + name/email stacked + gear (sidebar footers).
   * `icon`  — just avatar + gear (top bars). Name used as title-attr tooltip.
   */
  layout?: 'full' | 'icon';
  className?: string;
}

/**
 * SVG-inline gear icon (kept out of lucide-react so this package has no
 * icon-library dep). ~14px, currentColor stroke.
 */
function GearIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <circle cx="8" cy="8" r="2.25" />
      <path d="M8 1.5v1.8M8 12.7v1.8M14.5 8h-1.8M3.3 8H1.5M12.6 3.4l-1.3 1.3M4.7 11.3l-1.3 1.3M12.6 12.6l-1.3-1.3M4.7 4.7L3.4 3.4" />
    </svg>
  );
}

export function AccountWidget({
  avatar,
  name,
  email,
  onSettingsClick,
  isLoading,
  layout = 'full',
  className,
}: AccountWidgetProps) {
  const displayName = name ?? 'Account';

  if (layout === 'icon') {
    // Compact layout relies on Clerk's <UserButton /> built-in "Manage
    // account" menu item for settings — no explicit gear, less chrome.
    return (
      <div className={cn('flex items-center', className)} title={name ?? undefined}>
        {avatar}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'group/account flex items-center gap-2.5 rounded-md px-2 py-2 transition-colors hover:bg-[var(--l3-violet-soft)]',
        className,
      )}
    >
      {avatar}
      <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
        {isLoading ? (
          <Skeleton className="h-4 w-20" />
        ) : (
          <>
            <div className="truncate text-sm font-medium leading-tight">
              {displayName}
            </div>
            {email && (
              <div className="mt-0.5 truncate text-[0.68rem] text-muted-foreground">
                {email}
              </div>
            )}
          </>
        )}
      </div>
      {!isLoading && onSettingsClick && (
        <button
          type="button"
          onClick={onSettingsClick}
          aria-label="User settings"
          className="shrink-0 cursor-pointer rounded-md p-1 text-muted-foreground transition-colors hover:bg-[var(--l3-violet-soft)] hover:text-[var(--l3-violet)] group-data-[collapsible=icon]:hidden"
        >
          <GearIcon className="size-3.5" />
        </button>
      )}
    </div>
  );
}
